<template>
  <div id="agent_store">
    <el-config-provider :locale="nowLocale">
      <router-view />
      <LoginDialog />
      <ResponsiveSystemDialog />
    </el-config-provider>
  </div>
</template>

<script>
import i18n from '@/locales/i18n'
import zhLocale from 'element-plus/es/locale/lang/zh-cn'
import twLocale from 'element-plus/es/locale/lang/zh-tw'
import enLocale from 'element-plus/es/locale/lang/en'
import { initTheme } from '@/utils/theme'
import { mapActions } from 'vuex'
import LoginDialog from '@/components/auth/LoginDialog.vue'
import ResponsiveSystemDialog from '@/components/ResponsiveSystemDialog.vue'
const debounce = (fn, delay) => {
  let timer = null
  return function () {
    const context = this
    const args = arguments
    clearTimeout(timer)
    timer = setTimeout(function () {
      fn.apply(context, args)
    }, delay)
  }
}

const _ResizeObserver = window.ResizeObserver
window.ResizeObserver = class ResizeObserver extends _ResizeObserver {
  constructor(callback) {
    callback = debounce(callback, 16)
    super(callback)
  }
}

export default {
  name: 'App',
  components: {
    LoginDialog,
    ResponsiveSystemDialog
  },
  data() {
    return {
      localeList: {
        'en-us': enLocale,
        'zh-cn': zhLocale,
        'zh-tw': twLocale
      }
    }
  },
  computed: {
    nowLocale() {
      this.setLanguageFormat('en-us')
      return this.localeList[i18n.global.locale.value]
    },
    nowTheme() {
      const themeMode = this.$store.getters.themeMode
      return themeMode
    }
  },
  created() {
    // 主题初始化，传入 store
    initTheme(this.$store)
    if (this.$store.getters['user/accessToken']) {
      this.$websocket.init(this)
    }
  },
  methods: {
    ...mapActions({
      setLanguageFormat: 'settings/setLanguageFormat',
      setThemeMode: 'theme/setThemeMode'
    })
  }
}
</script>

<style lang="scss">
@import "@/views/mobile/shared/breakpoints";
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
}

#app {
  margin: 0 !important;
  padding: 0 !important;
  font-family: Helvetica Neue, Helvetica, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.el-overlay-dialog {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  .el-dialog {
    margin: 0;

    .el-dialog__body {
      max-height: 90vh
    }
  }
}

// Scrollbar visibility follows device identity, not the portrait/landscape layout.
@mixin hide-touch-scrollbars {
  &,
  * {
    scrollbar-width: none !important;
    scrollbar-gutter: auto !important;
  }

  &::-webkit-scrollbar,
  *::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  .el-scrollbar__bar {
    display: none !important;
  }
}

@media (max-width: 768px) {
  html:not([data-device='ipad']) {
    @include hide-touch-scrollbars;
  }
}

html[data-device='ipad'] {
  @include hide-touch-scrollbars;
}

@include mobile-or-ipad {
  @media (any-hover: none) {
    // Passive hints must not obscure touch controls. Mouse hints remain available.
    .excalidraw-tooltip, .mantine-Tooltip-tooltip { display: none !important; }
    [data-tooltip]:hover::after { content: none !important; display: none !important; }
  }
}

// Mobile controls use the compact visual scale consistently. Content cards,
// tiles and option rows keep their own dimensions because their button is the
// content surface rather than a standalone action.
@mixin compact-mobile-controls {
  --el-component-size-small: 32px;
  --van-button-small-height: 32px;
  --van-button-small-font-size: 13px;

  :is(button, .el-button, .van-button):not(:where(
    [class*='backdrop'],
    .folder-card, .period-card, .mobile-skill-card, .mobile-category-card,
    .mobile-home-tool, .mobile-agent-drawer-item,
    .mobile-knowledge-home__recent-card, .mobile-knowledge-home__project-cover,
    .recent-card, .quick-card, .case-detail-gallery-item,
    .external-skill-card, .external-skill-compact-item, .external-skill-example-dot,
    .web-image-asset-thumb, .chat-video-reference-preview,
    .pai-message-image, .media-thumbnail,
    .frame-main, .card-main, .reference-add-tile, .panel-add, .deck-add, .deck-more,
    .export-watermark-tile, .export-watermark-overlay, .export-asset-item,
    .asset-row, .media-canvas-ocr-region,
    .model-option, .reference-mode-option, .style-option, .selector-item,
    .clarification-option, .media-canvas-high-definition-option,
    .media-canvas-image-agent-option
  )),
  .toolbar-pill,
  .stop-btn,
  .btn-box,
  .external-skill-selected-clear {
    // These declarations intentionally beat the older scoped 44/48px rules.
    box-sizing: border-box;
    min-width: 32px !important;
    max-width: 100%;
    min-height: 32px !important;
    height: auto !important;
    padding: 4px 8px !important;
    font-size: 13px !important;
    line-height: 20px !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
  }

  .el-button > span,
  .van-button__content {
    min-width: 0;
    line-height: inherit;
    white-space: inherit;
    overflow-wrap: inherit;
  }

  .el-button.is-circle,
  .el-dialog__headerbtn,
  .el-drawer__close-btn,
  .el-message-box__headerbtn,
  .icon-btn, .icon-button, .icon-only-button, .plain-icon-btn, .icon-action-btn,
  .nickname-edit, .top-icon, .mobile-agent-history-button,
  .mobile-document-icon-button, .mobile-web-actions,
  .mobile-home-actions button,
  .submit-btn, .stop-btn, .upload-img-box,
  .settings-control-pill, .external-skill-selected-clear,
  .mobile-drawer-close, .dialog-close, .purchase-close,
  .arrow-button, .panel-close-btn,
  .back-to-bottom-btn,
  .selection-toolbar-btn-icon,
  .excalidraw .ToolIcon_type_button .ToolIcon__icon {
    box-sizing: border-box;
    flex: 0 0 32px !important;
    width: 32px !important;
    min-width: 32px !important;
    height: 32px !important;
    min-height: 32px !important;
    padding: 0 !important;
  }

  .el-button.is-circle { border-radius: 50%; }

  .toolbar-pill > .el-icon,
  .submit-btn > .svg-icon,
  .mobile-home-actions button > .icon {
    font-size: 18px;
  }
}

html[data-device='phone'] {
  @include compact-mobile-controls;
}

html[data-device='ipad'] {
  @include compact-mobile-controls;
}

// Desktop responsive previews use the same compact controls as narrow phones.
@media (max-width: 768px) {
  html:not([data-device='pc']):not([data-device='phone']):not([data-device='ipad']) {
    @include compact-mobile-controls;
  }
}
</style>
